import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Card, CircularProgress, FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, Stack,
  Switch, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  DownloadRounded as DownloadIcon,
  RestartAltRounded as ResetIcon,
  SendRounded as SendIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { BlockHeading } from '@/components/shell';
import { httpClient } from '@/utils/httpClient';
import { CodeBlock, Disclosure } from '@/components/shell';
import { ImageDropzone } from './ImageDropzone';
import { Metric, MetricRow } from './Metric';
import { compressImage, floatsToBase64 } from './testUtils';

/* ── Shapes of the face endpoints' responses ── */

interface FaceAttributes {
  estimatedAge: number;
  gender: string;
  genderConfidence: number;
}

interface BoundingBox { x: number; y: number; width: number; height: number }

interface ComparedFace {
  confidence?: number;
  boundingBox?: BoundingBox;
  attributes?: FaceAttributes;
}

interface CompareResult {
  verified: boolean;
  similarity?: number;
  distance?: number;
  face1?: ComparedFace;
  face2?: ComparedFace;
  processingTimeMs?: number;
}

interface DetectedFace {
  faceDetected: boolean;
  confidence: number;
  boundingBox?: BoundingBox;
  embedding?: number[];
  attributes?: FaceAttributes;
}

interface FacesResult {
  results?: DetectedFace[];
  processingTimeMs?: number;
}

/** One face endpoint called with a body: its result, its error and whether it is running. */
const useFaceRequest = <R,>(endpoint: string) => {
  const t = useTranslate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<R | null>(null);
  const [error, setError] = useState('');

  const run = async (body: object) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await httpClient.post<R>(endpoint, body);
      setResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || t('screens.testPage.unknownError'));
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setResult(null); setError(''); };

  return { loading, result, error, run, clear };
};

/** One image for a face endpoint: its base64 for the request and its preview. */
const useFaceImage = () => {
  const [image, setImage] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const set = (b64: string | null) => {
    setImage(b64);
    setPreview(b64 ? `data:image/jpeg;base64,${b64}` : null);
  };
  return { image, preview, set };
};

/** The dropzone of a face endpoint: images only, the front camera first. */
const FaceDropzone: React.FC<{ label: string; face: ReturnType<typeof useFaceImage> }> = ({ label, face }) => (
  <ImageDropzone
    label={label}
    preview={face.preview}
    accept="image/*"
    onFile={async (file) => {
      if (!file.type.startsWith('image/')) return;
      face.set(await compressImage(file));
    }}
    onRemove={() => face.set(null)}
    onCapture={face.set}
    facingMode="user"
    idealWidth={1280}
    previewMaxHeight={140}
  />
);

/* ── API documentation for integrators ── */

const ApiInfoPanel: React.FC<{
  endpoint: string; method: string; requestExample: string; responseExample: string; description: string;
}> = ({ endpoint, method, requestExample, responseExample, description }) => {
  const t = useTranslate();
  const theme = useTheme();

  return (
    <Disclosure label={t('test.face_recognition.api_info')}>
      <Stack spacing={1.5}>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        <Typography variant="body2" sx={{ fontFamily: theme.custom.text.mono, fontWeight: 500, overflowWrap: 'anywhere' }}>
          {method} {endpoint}
        </Typography>
        <Typography variant="subtitle2">{t('test.face_recognition.request_body')}</Typography>
        <CodeBlock text={requestExample} />
        <Typography variant="subtitle2">{t('test.face_recognition.response_example')}</Typography>
        <CodeBlock text={responseExample} />
        <Alert severity="info">{t('test.face_recognition.auth_note')}</Alert>
      </Stack>
    </Disclosure>
  );
};

/* ── The frame every face tool shares ── */

const FaceSection: React.FC<{
  id: string;
  title: string;
  subtitle?: string;
  /** Something to clear: shows the reset button. */
  dirty: boolean;
  onReset: () => void;
  children: React.ReactNode;
}> = ({ id, title, subtitle, dirty, onReset, children }) => {
  const t = useTranslate();
  return (
    <Box component="section" aria-labelledby={id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BlockHeading
        id={id}
        title={title}
        subtitle={subtitle}
        action={dirty ? (
          <Tooltip title={t('test.clear')}>
            <IconButton aria-label={t('test.clear')} onClick={onReset}><ResetIcon /></IconButton>
          </Tooltip>
        ) : undefined}
      />
      <Card sx={{ p: { xs: 2, sm: 2.5 }, flex: 1 }}>
        <Stack spacing={2}>{children}</Stack>
      </Card>
    </Box>
  );
};

const RunButton: React.FC<{ label: string; loading: boolean; disabled: boolean; onClick: () => void }> = ({
  label, loading, disabled, onClick,
}) => (
  <Button
    variant="contained"
    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
    disabled={disabled || loading}
    onClick={onClick}
  >
    {label}
  </Button>
);

const ResponseJson: React.FC<{ result: unknown }> = ({ result }) => {
  const t = useTranslate();
  return (
    <Disclosure label={t('screens.testPage.responseJson')}>
      <CodeBlock text={JSON.stringify(result, null, 2)} />
    </Disclosure>
  );
};

const useGenderLabel = () => {
  const t = useTranslate();
  return (attrs: FaceAttributes) =>
    attrs.gender === 'Male' ? t('test.face_recognition.male') : t('test.face_recognition.female');
};

const percent = (value: number, digits = 0) => `${(value * 100).toFixed(digits)}%`;
const boundingBoxText = (box: BoundingBox) => `${box.x},${box.y} — ${box.width}x${box.height}`;

/* ── Face comparison ── */

type CompareMode = 'images' | 'document-selfie';

const similarityTone = (value: number): 'success' | 'warning' | 'error' =>
  value >= 70 ? 'success' : value >= 40 ? 'warning' : 'error';

const FaceCompareSection: React.FC = () => {
  const t = useTranslate();
  const theme = useTheme();
  const genderLabel = useGenderLabel();
  const [mode, setMode] = useState<CompareMode>('images');
  const first = useFaceImage();
  const second = useFaceImage();
  const [faceVector, setFaceVector] = useState(false);
  const request = useFaceRequest<CompareResult>('/admin/test/face/compare');
  const data = request.result;

  const reset = () => { first.set(null); second.set(null); request.clear(); };

  const labels = mode === 'document-selfie'
    ? [t('test.face_recognition.document'), t('test.face_recognition.selfie')]
    : [t('test.face_recognition.image1'), t('test.face_recognition.image2')];

  return (
    <FaceSection
      id="face-compare"
      title={t('test.face_recognition.compare_title')}
      dirty={Boolean(first.image || second.image || data)}
      onReset={reset}
    >
      <TextField
        select
        label={t('screens.testPage.compareMode')}
        value={mode}
        onChange={(e) => { setMode(e.target.value as CompareMode); reset(); }}
        sx={{ maxWidth: { sm: 320 } }}
      >
        <MenuItem value="images">{t('test.face_recognition.mode_images')}</MenuItem>
        <MenuItem value="document-selfie">{t('test.face_recognition.mode_doc_selfie')}</MenuItem>
      </TextField>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FaceDropzone label={labels[0]} face={first} />
        <FaceDropzone label={labels[1]} face={second} />
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <FormControlLabel
          control={<Switch checked={faceVector} onChange={(e) => setFaceVector(e.target.checked)} />}
          label={t('test.face_vector')}
        />
        <Box sx={{ flex: 1 }} />
        <RunButton
          label={t('test.face_recognition.compare_btn')}
          loading={request.loading}
          disabled={!first.image || !second.image}
          onClick={() => request.run({ mode, image1: first.image, image2: second.image, faceVector })}
        />
      </Box>

      {request.error && <Alert severity="error">{request.error}</Alert>}

      {data && (
        <Stack spacing={2}>
          <Alert severity={data.verified ? 'success' : 'error'}>
            {data.verified ? t('test.face_recognition.verified') : t('test.face_recognition.not_verified')}
          </Alert>

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">{t('test.face_recognition.similarity')}</Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontFamily: theme.custom.text.mono, color: `${similarityTone(data.similarity ?? 0)}.dark` }}
              >
                {data.similarity?.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(data.similarity || 0, 100)}
              color={similarityTone(data.similarity ?? 0)}
              aria-label={t('test.face_recognition.similarity')}
              sx={{ height: 10, borderRadius: theme.custom.radius.full }}
            />
          </Box>

          <MetricRow>
            <Metric label={t('test.face_recognition.distance')} value={data.distance?.toFixed(4)} mono />
            <Metric label={t('test.face_recognition.processing_time')} value={`${data.processingTimeMs} ms`} />
          </MetricRow>

          {([['face1', labels[0]], ['face2', labels[1]]] as const).map(([key, label]) => {
            const face = data[key];
            if (!face) return null;
            return (
              <Box key={key}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
                <MetricRow>
                  <Metric
                    label={t('test.face_recognition.detection')}
                    value={face.confidence ? percent(face.confidence) : '—'}
                  />
                  {face.attributes && (
                    <>
                      <Metric label={t('test.face_recognition.age')} value={`~${face.attributes.estimatedAge}`} />
                      <Metric
                        label={t('screens.testPage.gender')}
                        value={`${genderLabel(face.attributes)} (${percent(face.attributes.genderConfidence)})`}
                      />
                    </>
                  )}
                  {face.boundingBox && (
                    <Metric label={t('screens.testPage.boundingBox')} value={boundingBoxText(face.boundingBox)} mono />
                  )}
                </MetricRow>
              </Box>
            );
          })}
        </Stack>
      )}

      {data && <ResponseJson result={data} />}

      <ApiInfoPanel
        endpoint="/api/v2/face/compare"
        method="POST"
        description={t('test.face_recognition.compare_api_desc')}
        requestExample={mode === 'document-selfie'
          ? JSON.stringify({ document: '<base64>', selfie: '<base64>', face_vector: false }, null, 2)
          : JSON.stringify({ image1: '<base64>', image2: '<base64>', face_vector: false }, null, 2)}
        responseExample={JSON.stringify({
          verified: true,
          similarity: 87.34,
          distance: 0.1266,
          face1: {
            detected: true, confidence: 0.998,
            boundingBox: { x: 45, y: 32, width: 120, height: 150 },
            attributes: { estimatedAge: 34, gender: 'Male', genderConfidence: 0.96 },
          },
          face2: {
            detected: true, confidence: 0.995,
            boundingBox: { x: 60, y: 28, width: 115, height: 145 },
            attributes: { estimatedAge: 35, gender: 'Male', genderConfidence: 0.94 },
          },
          processingTimeMs: 342,
        }, null, 2)}
      />
    </FaceSection>
  );
};

/* ── Embedding vector and its QR code ── */

const EmbeddingDisplay: React.FC<{ embedding: number[] }> = ({ embedding }) => {
  const t = useTranslate();
  const theme = useTheme();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!embedding || embedding.length === 0) return;
    QRCode.toDataURL(floatsToBase64(embedding), { errorCorrectionLevel: 'L', margin: 1, width: 350 })
      .then((url) => setQrDataUrl(url))
      .catch(() => setQrDataUrl(null));
  }, [embedding]);

  const downloadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = 'face_embedding_qr.png';
    a.click();
  };

  return (
    <Stack spacing={0.5}>
      <Disclosure label={t('screens.testPage.vectorFloats', { label: t('test.face_recognition.vector_label'), count: embedding.length })}>
        <CodeBlock
          text={`[${embedding.map((v) => v.toFixed(6)).join(', ')}]`}
          copyText={JSON.stringify(embedding)}
        />
      </Disclosure>
      {qrDataUrl && (
        <Disclosure label={t('screens.testPage.qrCode')}>
          <Stack spacing={1} alignItems="flex-start">
            <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: theme.custom.radius.small, p: 1, backgroundColor: 'background.paper' }}>
              <img src={qrDataUrl} alt={t('screens.testPage.qrAlt')} style={{ display: 'block', width: 200, height: 200 }} />
            </Box>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadQr}>
              {t('test.face_recognition.download_qr')}
            </Button>
          </Stack>
        </Disclosure>
      )}
    </Stack>
  );
};

/* ── Face embedding ── */

const FaceEmbedSection: React.FC = () => {
  const t = useTranslate();
  const image = useFaceImage();
  const request = useFaceRequest<FacesResult>('/admin/test/face/embed');
  const face = request.result?.results?.[0];

  return (
    <FaceSection
      id="face-embed"
      title={t('test.face_recognition.embed_title')}
      dirty={Boolean(image.image || request.result)}
      onReset={() => { image.set(null); request.clear(); }}
    >
      <FaceDropzone label={t('test.face_recognition.image')} face={image} />
      <RunButton
        label={t('test.face_recognition.embed_btn')}
        loading={request.loading}
        disabled={!image.image}
        onClick={() => request.run({ image: image.image })}
      />

      {request.error && <Alert severity="error">{request.error}</Alert>}

      {face && (!face.faceDetected ? (
        <Alert severity="warning">{t('test.face_recognition.no_face_detected')}</Alert>
      ) : (
        <Stack spacing={1.5}>
          <MetricRow>
            <Metric label={t('test.face_recognition.detection')} value={percent(face.confidence, 1)} />
            {face.boundingBox && (
              <Metric label={t('screens.testPage.boundingBox')} value={boundingBoxText(face.boundingBox)} mono />
            )}
            <Metric label={t('test.face_recognition.processing_time')} value={`${request.result?.processingTimeMs} ms`} />
          </MetricRow>
          {face.attributes && <FaceAttributesMetrics attrs={face.attributes} />}
          {face.embedding && <EmbeddingDisplay embedding={face.embedding} />}
        </Stack>
      ))}

      {request.result && <ResponseJson result={request.result} />}

      <ApiInfoPanel
        endpoint="/api/v2/face/embed"
        method="POST"
        description={t('test.face_recognition.embed_api_desc')}
        requestExample={JSON.stringify({ image: '<base64>' }, null, 2)}
        responseExample={JSON.stringify({
          results: [{
            faceDetected: true, confidence: 0.997,
            boundingBox: { x: 45, y: 32, width: 120, height: 150 },
            embedding: [0.0234, -0.0156, '... (512 floats)'],
            attributes: { estimatedAge: 34, gender: 'Male', genderConfidence: 0.96 },
          }],
          processingTimeMs: 185,
        }, null, 2)}
      />
    </FaceSection>
  );
};

const FaceAttributesMetrics: React.FC<{ attrs: FaceAttributes }> = ({ attrs }) => {
  const t = useTranslate();
  const genderLabel = useGenderLabel();
  return (
    <MetricRow>
      <Metric label={t('test.face_recognition.age')} value={`~${attrs.estimatedAge}`} />
      <Metric label={t('screens.testPage.gender')} value={`${genderLabel(attrs)} (${percent(attrs.genderConfidence)})`} />
    </MetricRow>
  );
};

/* ── Age and gender ── */

const FaceAttributesSection: React.FC = () => {
  const t = useTranslate();
  const genderLabel = useGenderLabel();
  const image = useFaceImage();
  const request = useFaceRequest<FacesResult>('/admin/test/face/attributes');
  const face = request.result?.results?.[0];

  return (
    <FaceSection
      id="face-attributes"
      title={t('test.face_recognition.attributes_title')}
      subtitle={t('test.face_recognition.attributes_desc')}
      dirty={Boolean(image.image || request.result)}
      onReset={() => { image.set(null); request.clear(); }}
    >
      <FaceDropzone label={t('test.face_recognition.image')} face={image} />
      <RunButton
        label={t('test.face_recognition.estimate_btn')}
        loading={request.loading}
        disabled={!image.image}
        onClick={() => request.run({ image: image.image })}
      />

      {request.error && <Alert severity="error">{request.error}</Alert>}

      {face && (!face.faceDetected ? (
        <Alert severity="warning">{t('test.face_recognition.no_face_detected')}</Alert>
      ) : (
        <Stack spacing={1.5}>
          {face.attributes && (
            <MetricRow>
              <Box>
                <Typography variant="figure" component="div">{face.attributes.estimatedAge}</Typography>
                <Typography variant="body2" color="text.secondary">{t('test.face_recognition.estimated_age')}</Typography>
              </Box>
              <Box>
                <Typography variant="figure" component="div">{genderLabel(face.attributes)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {percent(face.attributes.genderConfidence)} {t('test.face_recognition.confidence')}
                </Typography>
              </Box>
            </MetricRow>
          )}
          <MetricRow>
            <Metric label={t('test.face_recognition.detection')} value={percent(face.confidence, 1)} />
            <Metric label={t('test.face_recognition.processing_time')} value={`${request.result?.processingTimeMs} ms`} />
          </MetricRow>
        </Stack>
      ))}

      {request.result && <ResponseJson result={request.result} />}

      <ApiInfoPanel
        endpoint="/api/v2/face/attributes"
        method="POST"
        description={t('test.face_recognition.attributes_api_desc')}
        requestExample={JSON.stringify({ image: '<base64>' }, null, 2)}
        responseExample={JSON.stringify({
          results: [{
            faceDetected: true, confidence: 0.997,
            boundingBox: { x: 45, y: 32, width: 120, height: 150 },
            attributes: { estimatedAge: 34, gender: 'Male', genderConfidence: 0.96 },
          }],
          processingTimeMs: 95,
        }, null, 2)}
      />
    </FaceSection>
  );
};

/** The face recognition tools: compare two faces, extract an embedding, estimate age and gender. */
export const FaceTestPanel: React.FC = () => (
  <Stack spacing={3}>
    <FaceCompareSection />
    {/* A Stack strips its children's margins, which Grid's negative gutter needs. */}
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}><FaceEmbedSection /></Grid>
        <Grid item xs={12} md={6}><FaceAttributesSection /></Grid>
      </Grid>
    </Box>
  </Stack>
);
